import { PricebookDraftDiff } from "../../../../types/price-book.type";
import { numberWithCommas } from "../../../../utils";
import { formatDiscountPercent } from "../constants";

export type DraftStepKey = "copy" | "edit" | "test" | "apply";

/** One of the four steps a draft goes through on its way onto the source. */
export interface DraftStep {
  key: DraftStepKey;
  label: string;
  /** Where the step stands; empty while the comparison is not read yet. */
  caption: string;
  isCompleted: boolean;
}

const CAPTION_SEPARATOR = " · ";

/** Products the draft adds, takes off or changes against its source. */
export const countDraftLineChanges = (diff: PricebookDraftDiff): number =>
  diff.summary.added_count +
  diff.summary.removed_count +
  diff.summary.changed_count;

const buildEditStep = (diff: PricebookDraftDiff | null): DraftStep => {
  const step: DraftStep = {
    key: "edit",
    label: "แก้ราคาในร่าง",
    caption: "",
    isCompleted: false,
  };
  if (!diff) return step;
  const lineChangeCount = countDraftLineChanges(diff);
  const parts: string[] = [];
  if (diff.discount_percent.old !== diff.discount_percent.new) {
    parts.push(
      `ส่วนลดสมุด ${formatDiscountPercent(diff.discount_percent.old)} → ${formatDiscountPercent(diff.discount_percent.new)}`,
    );
  }
  if (lineChangeCount > 0) {
    parts.push(`ต่างจากต้นฉบับ ${numberWithCommas(lineChangeCount)} รายการ`);
  }
  return {
    ...step,
    caption: parts.join(CAPTION_SEPARATOR) || "ยังไม่ต่างจากต้นฉบับ",
    isCompleted: parts.length > 0,
  };
};

const buildTestStep = (diff: PricebookDraftDiff | null): DraftStep => {
  const step: DraftStep = {
    key: "test",
    label: "ผูกร้านทดสอบ (00-HO)",
    caption: "",
    isCompleted: false,
  };
  if (!diff) return step;
  const isBound = diff.draft_shop_count > 0;
  return {
    ...step,
    caption: isBound
      ? `ผูกแล้ว ${numberWithCommas(diff.draft_shop_count)} ร้าน`
      : "ยังไม่ได้ผูกร้าน",
    isCompleted: isBound,
  };
};

/**
 * ทำสำเนา → แก้ราคา → ผูกร้านทดสอบใน 00-HO → นำไปใช้. The last step never completes here: once
 * applied, the draft is gone.
 */
export const buildDraftSteps = (
  sourceCode: string,
  diff: PricebookDraftDiff | null,
): DraftStep[] => [
  {
    key: "copy",
    label: "ทำสำเนา",
    caption: `จาก ${sourceCode}`,
    isCompleted: true,
  },
  buildEditStep(diff),
  buildTestStep(diff),
  {
    key: "apply",
    label: `นำไปใช้กับ ${sourceCode}`,
    caption: "ตรวจความต่าง แล้วพิมพ์รหัสยืนยัน",
    isCompleted: false,
  },
];

/** The step to highlight: the first one not done yet. */
export const findActiveDraftStep = (steps: DraftStep[]): number =>
  steps.findIndex((step) => !step.isCompleted);

interface ApplyBlockerInput {
  hasUnsavedChanges: boolean;
  diff: PricebookDraftDiff | null;
  loading: boolean;
  errorMessage: string;
}

/**
 * Why "นำไปใช้" is shut, or "" when it may open: the API applies what is saved, refuses a draft
 * whose source moved, and the dialog needs the comparison to show.
 */
export const resolveApplyBlocker = ({
  hasUnsavedChanges,
  diff,
  loading,
  errorMessage,
}: ApplyBlockerInput): string => {
  if (hasUnsavedChanges) return "บันทึกหรือยกเลิกการแก้ไขก่อน";
  if (errorMessage) return errorMessage;
  if (loading || !diff) return "กำลังอ่านความต่างจากสมุดราคาต้นฉบับ";
  if (diff.source_changed) {
    return "สมุดราคาต้นฉบับถูกแก้หลังทำสำเนา ต้องทำสำเนาใหม่";
  }
  return "";
};
